/**
 * Life4Earth V2 — React hook for loading workflow requests from the API.
 *
 * Replaces: localStorage reads from l4e_workflow_requests
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllWorkflowRequests,
  workflowResponseToStoreShape,
} from "./workflowRequests";
import type { WorkflowRequestResponse } from "./workflowRequests";
import {
  fetchForKey,
  invalidateAndRefetch,
  CACHE_UPDATE_EVENT,
  QUERY_KEYS,
} from "./queryCache";

export interface UseApiWorkflowsResult {
  raw: WorkflowRequestResponse[];
  workflows: Record<string, any>[];
  loading: boolean;
  error: string | null;
  source: "api" | "seed" | "loading";
  refetch: () => void;
}

export function useApiWorkflows(): UseApiWorkflowsResult {
  const [raw, setRaw] = useState<WorkflowRequestResponse[]>([]);
  const [workflows, setWorkflows] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "seed" | "loading">("loading");
  const mountedRef = useRef(true);

  const refetch = useCallback(() => {
    invalidateAndRefetch(QUERY_KEYS.workflows);
  }, []);

  const load = useCallback(() => {
    setError(null);
    fetchForKey(
      QUERY_KEYS.workflows,
      async () => {
        const apiItems = await fetchAllWorkflowRequests();
        return { raw: apiItems, transformed: apiItems.map(workflowResponseToStoreShape) };
      }
    )
      .then((result) => {
        if (!mountedRef.current) return;
        setRaw(result.raw);
        setWorkflows(result.transformed);
        setSource("api");
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.warn("[useApiWorkflows] API unreachable:", err.message);
        setError(err.message);
        setRaw([]);
        setWorkflows([]);
        setSource("seed");
        setLoading(false);
      });
  }, []);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Subscribe to cache invalidation
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === QUERY_KEYS.workflows) {
        load();
      }
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [load]);

  return { raw, workflows, loading, error, source, refetch };
}
